"use strict";
/**
 * How a «Note spese» expense is filled in, by type and by payment (decision 145, #497): one set of
 * rules the module's server enforces and the capture form follows, so the two never disagree.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EXPENSE_FORM_RULES = exports.ADVANCE_METHODS = exports.PAYMENT_WAYS = void 0;
exports.paymentOf = paymentOf;
exports.paymentWayOf = paymentWayOf;
exports.isTraceable = isTraceable;
exports.isReimbursable = isReimbursable;
exports.expenseFormKind = expenseFormKind;
exports.mileageTotalCents = mileageTotalCents;
/** «Come hai pagato?»: the first payment question; payer and method follow from it. */
exports.PAYMENT_WAYS = [
    'company-card',
    'personal-card',
    'transfer',
    'company-transfer',
    'cash',
    'director-advance',
    'other',
];
/** The method an administrator's advance was paid with is asked separately; the others are fixed. */
exports.ADVANCE_METHODS = ['card', 'transfer', 'cash'];
function paymentOf(way, advanceMethod = 'card') {
    switch (way) {
        case 'company-card':
            return { payer: 'company-card', paymentMethod: 'card' };
        case 'personal-card':
            return { payer: 'personal', paymentMethod: 'card' };
        case 'transfer':
            return { payer: 'personal', paymentMethod: 'transfer' };
        case 'company-transfer':
            return { payer: 'company-transfer', paymentMethod: 'transfer' };
        case 'cash':
            return { payer: 'personal', paymentMethod: 'cash' };
        case 'director-advance':
            return { payer: 'director-advance', paymentMethod: advanceMethod };
        case 'other':
            return { payer: 'personal', paymentMethod: 'other' };
    }
}
/** The answer to «Come hai pagato?» a stored payer and method mean. */
function paymentWayOf(payer, paymentMethod) {
    if (payer === 'company-card')
        return 'company-card';
    if (payer === 'company-transfer')
        return 'company-transfer';
    if (payer === 'director-advance')
        return 'director-advance';
    switch (paymentMethod) {
        case 'card':
            return 'personal-card';
        case 'transfer':
            return 'transfer';
        case 'cash':
            return 'cash';
        default:
            return 'other';
    }
}
/** Traceable is derived, never asked: everything but cash (decision 145). */
function isTraceable(paymentMethod) {
    return paymentMethod !== 'cash';
}
/**
 * Reimbursed to the person: traceable, and not paid by the company. An untraceable expense is still
 * recorded, flagged and not reimbursed (decision 145). The administrator's advance is never: it
 * has its own total and its own settlement (decision 218, option C).
 */
function isReimbursable(payer, paymentMethod) {
    return (payer !== 'company-card' &&
        payer !== 'company-transfer' &&
        payer !== 'director-advance' &&
        isTraceable(paymentMethod));
}
const KIND_BY_CATEGORY = {
    'mileage-reimbursement': 'mileage',
    'travel-meals': 'meal',
    'work-meals': 'meal',
    'entertainment-meals': 'meal',
    entertainment: 'meal',
    lodging: 'lodging',
    transportation: 'transport',
};
function expenseFormKind(categoryCode) {
    return (categoryCode && KIND_BY_CATEGORY[categoryCode]) || 'standard';
}
/** The fields each kind requires beyond the common ones, and those it hides. */
exports.EXPENSE_FORM_RULES = {
    // The total is km × the rate, read-only; nothing to buy from a supplier, no document, no tax,
    // and paid back to the person by transfer.
    mileage: {
        requires: ['km', 'aciRate', 'route'],
        hides: [
            'supplierName',
            'supplierVat',
            'documentNumber',
            'documentType',
            'taxLines',
            'payment',
            'nonTaxable',
        ],
    },
    meal: { requires: ['peopleInvolved', 'businessReason'], hides: [] },
    lodging: { requires: ['referenceDays', 'place'], hides: [] },
    transport: { requires: ['route'], hides: [] },
    standard: { requires: [], hides: [] },
};
const DECIMAL = /^(\d+)(?:\.(\d+))?$/;
function scaled(value, places) {
    const match = DECIMAL.exec(value.trim());
    if (match === null || (match[2]?.length ?? 0) > places)
        return null;
    return (BigInt(match[1]) * 10n ** BigInt(places) +
        BigInt((match[2] ?? '').padEnd(places, '0') || '0'));
}
/**
 * A mileage total in cents: km (up to 2 decimals) × the rate in €/km (up to 6 decimals), rounded
 * half up to the cent, in integers. Null when either is not a plain positive decimal.
 */
function mileageTotalCents(km, aciRate) {
    const kmHundredths = scaled(km, 2);
    const rateMicro = scaled(aciRate, 6);
    if (kmHundredths === null || rateMicro === null)
        return null;
    // km/100 × rate/1e6 € = km × rate / 1e8 € = km × rate / 1e6 cents.
    const product = kmHundredths * rateMicro;
    const cents = (product + 500000n) / 1000000n;
    return Number(cents);
}
//# sourceMappingURL=expense-form-rules.js.map